import { apiRequest } from "./client";

// The subset of Video columns the backend's PLAYLIST_INCLUDE selects.
// Mirrors `playlists.service.ts` — kept narrow so the FE can render
// playlist cards without dragging the full Video shape around.
export type PlaylistItemVideo = {
  id: string;
  title: string;
  thumbUrl: string;
  duration: number;
  ageMin: number;
  ageMax: number;
  categories: string[];
  isPremium: boolean;
  generationStatus: string;
  parentId: string | null;
};

export type PlaylistItem = {
  id: string;
  playlistId: string;
  videoId: string;
  position: number;
  addedAt: string;
  video: PlaylistItemVideo;
};

export type Playlist = {
  id: string;
  name: string;
  color: string | null;
  parentId: string;
  createdAt: string;
  updatedAt: string;
  items: PlaylistItem[];
};

// All endpoints are JWT-gated and scoped to the calling parent. The backend
// returns 404 (not 403) when you ask about another family's playlist, so
// the FE should treat 404 here as either "doesn't exist" or "not yours".
export const playlistsApi = {
  list: (token: string): Promise<Playlist[]> =>
    apiRequest<Playlist[]>("/v1/playlists", { token }),

  get: (id: string, token: string): Promise<Playlist> =>
    apiRequest<Playlist>(`/v1/playlists/${id}`, { token }),

  create: (
    body: { name: string; color?: string | null },
    token: string,
  ): Promise<Playlist> =>
    apiRequest<Playlist>("/v1/playlists", {
      method: "POST",
      body,
      token,
    }),

  update: (
    id: string,
    body: { name?: string; color?: string | null },
    token: string,
  ): Promise<Playlist> =>
    apiRequest<Playlist>(`/v1/playlists/${id}`, {
      method: "PATCH",
      body,
      token,
    }),

  remove: (id: string, token: string): Promise<{ ok: boolean }> =>
    apiRequest<{ ok: boolean }>(`/v1/playlists/${id}`, {
      method: "DELETE",
      token,
    }),

  // Append videos to the playlist. Idempotent at the backend — re-adding
  // an existing video is a no-op. Returns the refreshed playlist.
  addItems: (
    id: string,
    videoIds: string[],
    token: string,
  ): Promise<Playlist> =>
    apiRequest<Playlist>(`/v1/playlists/${id}/items`, {
      method: "POST",
      body: { videoIds },
      token,
    }),

  removeItem: (
    id: string,
    videoId: string,
    token: string,
  ): Promise<Playlist> =>
    apiRequest<Playlist>(`/v1/playlists/${id}/items/${videoId}`, {
      method: "DELETE",
      token,
    }),

  // Rewrite the order. Pass the desired videoId sequence — the backend
  // positions them 0..N-1. videoIds not in the playlist are ignored.
  reorder: (
    id: string,
    videoIds: string[],
    token: string,
  ): Promise<Playlist> =>
    apiRequest<Playlist>(`/v1/playlists/${id}/items`, {
      method: "PATCH",
      body: { videoIds },
      token,
    }),
};
